import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { request } from '../lib/api.js';
import { NOTICE_KINDS, NOTICE_SURFACES, type NoticeKind, type NoticeSurface } from '@monrdv/shared';

/**
 * Notices — writing them, publishing them, and seeing who has agreed.
 *
 * Three things this screen exists to make possible, in the order the business will use them:
 *
 *   1. **Post something today.** A recall, an internal rule, the recurring issue everybody
 *      keeps hitting. If posting one needs a developer, none get posted.
 *   2. **Publish deliberately.** Publishing is a distinct act with a confirmation, because it
 *      is a statement the business is making to its own staff — and for a geolocation notice
 *      it is also what records that Law 25's notice requirement was met.
 *   3. **Chase the people who have not read it.** "Nobody has acknowledged this" and
 *      "everybody except Marc" are different facts, and only the second one is actionable.
 *
 * The one rule the interface must not hide: **a published version cannot be edited.** The
 * database refuses it. So the editor shows a published version read-only and offers "new
 * version" instead, and says plainly that publishing one asks everybody again.
 */

type NoticeRow = {
  id: string;
  key: string;
  kind: NoticeKind;
  status: string;
  live: {
    id: string;
    version: number;
    title: Record<string, string>;
    enforcement: string;
    audienceRoles: string[];
    surfaces: string[];
    publishedAt: string | null;
    acknowledged: number;
    owed: number;
  } | null;
  draft: { id: string; version: number } | null;
};

type Version = {
  id: string;
  version: number;
  title: Record<string, string>;
  body: Record<string, string>;
  enforcement: 'blocking' | 'acknowledge' | 'informational';
  audienceRoles: string[];
  surfaces: NoticeSurface[];
  publicUrl: string | null;
  publishedAt: string | null;
  supersededAt: string | null;
  publishedBy: string | null;
  editable: boolean;
};

type Trace = {
  key: string;
  version: number;
  publishedAt: string | null;
  surfaces: string[];
  audienceRoles: string[];
  owed: number;
  acknowledged: number;
  people: Array<{
    userId: string;
    name: string;
    roleKey: string;
    acknowledgedAt: string | null;
    surface: string | null;
    deviceAt: string | null;
  }>;
};

const KIND_LABEL: Record<NoticeKind, string> = {
  terms: 'Conditions d’utilisation',
  privacy: 'Vie privée',
  geolocation: 'Localisation',
  safety: 'Sécurité',
  rule: 'Règle interne',
  recall: 'Rappel',
  note: 'Note',
};

const ENFORCEMENT_LABEL: Record<string, string> = {
  blocking: 'Bloquant — l’application ne s’ouvre pas sans acceptation',
  acknowledge: 'À accuser réception — bandeau avec un bouton',
  informational: 'Informatif — bandeau à lire',
};

const SURFACE_LABEL: Record<string, string> = { portal: 'Portail', field: 'Terrain' };

export function NoticesScreen() {
  const search = useSearch({ strict: false }) as { notice?: string };
  const navigate = useNavigate();
  const [notices, setNotices] = useState<NoticeRow[] | null>(null);
  const [roles, setRoles] = useState<Array<{ key: string; name: string }>>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [list, team] = await Promise.all([
        request<{ notices: NoticeRow[] }>('/notices/admin'),
        request<{ roles?: Array<{ key: string; name: unknown }> }>('/roles').catch(() => ({ roles: [] })),
      ]);
      setNotices(list.notices);
      setRoles(
        (team.roles ?? []).map((role) => ({
          key: role.key,
          name: typeof role.name === 'string' ? role.name : ((role.name as Record<string, string>)?.fr ?? role.key),
        })),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Chargement impossible');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = notices?.find((notice) => notice.id === search.notice) ?? null;

  return (
    <div className="notices">
      <header className="notices__head">
        <div>
          <h1>Avis et conditions</h1>
          <p className="notices__lede">
            Ce que le personnel doit lire, et la preuve qu’il l’a lu. Une version publiée ne se modifie pas :
            une correction est une nouvelle version, que tout le monde doit accepter de nouveau.
          </p>
        </div>
        <button type="button" className="btn btn--primary" onClick={() => navigate({ search: { notice: 'new' } as never })}>
          Nouvel avis
        </button>
      </header>

      {error && <p className="notices__error">{error}</p>}

      <div className="notices__grid">
        <ul className="notices__list">
          {notices?.map((notice) => (
            <li key={notice.id} className={notice.id === search.notice ? 'on' : ''}>
              <button type="button" onClick={() => navigate({ search: { notice: notice.id } as never })}>
                <span className="notices__kind">{KIND_LABEL[notice.kind]}</span>
                <span className="notices__title">
                  {notice.live ? notice.live.title.fr : <em>brouillon</em>}
                </span>
                <span className="notices__meta">
                  {notice.live ? (
                    <>
                      v{notice.live.version} ·{' '}
                      <strong className={notice.live.acknowledged < notice.live.owed ? 'short' : 'complete'}>
                        {notice.live.acknowledged} / {notice.live.owed}
                      </strong>{' '}
                      · {notice.live.surfaces.map((s) => SURFACE_LABEL[s] ?? s).join(', ')}
                    </>
                  ) : (
                    'jamais publié'
                  )}
                  {notice.draft && ' · brouillon en cours'}
                  {notice.status !== 'active' && ' · retiré'}
                </span>
              </button>
            </li>
          ))}
          {notices?.length === 0 && <li className="notices__empty">Aucun avis.</li>}
        </ul>

        <div className="notices__panel">
          {search.notice === 'new' ? (
            <NoticeEditor
              roles={roles}
              onSaved={async () => {
                await load();
                navigate({ search: {} as never });
              }}
            />
          ) : selected ? (
            <NoticeDetail notice={selected} roles={roles} onChanged={load} />
          ) : (
            <p className="notices__hint">Choisissez un avis, ou créez-en un.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function NoticeDetail({
  notice,
  roles,
  onChanged,
}: {
  notice: NoticeRow;
  roles: Array<{ key: string; name: string }>;
  onChanged: () => void | Promise<void>;
}) {
  const [versions, setVersions] = useState<Version[] | null>(null);
  const [trace, setTrace] = useState<Trace | null>(null);
  const [editing, setEditing] = useState<Version | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    const detail = await request<{ versions: Version[] }>(`/notices/${notice.id}`);
    setVersions(detail.versions);
    if (notice.live) {
      setTrace(await request<Trace>(`/notices/${notice.id}/trace`).catch(() => null));
    } else {
      setTrace(null);
    }
  }, [notice.id, notice.live]);

  useEffect(() => {
    setEditing(null);
    setNote(null);
    void load();
  }, [load]);

  const publish = async (version: Version) => {
    setBusy(true);
    try {
      const result = await request<{ positionNoticeRecorded: boolean }>(`/notices/versions/${version.id}/publish`, {
        method: 'POST',
      });
      setNote(
        result.positionNoticeRecorded
          ? 'Publié. L’avis de localisation est maintenant au dossier : la position peut être activée dans les réglages.'
          : 'Publié. Le personnel visé devra l’accepter.',
      );
      await load();
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  const draft = versions?.find((version) => version.editable) ?? null;

  return (
    <div className="notices__detail">
      <h2>{notice.key}</h2>
      {note && <p className="notices__ok">{note}</p>}

      {editing ? (
        <NoticeEditor
          noticeId={notice.id}
          existing={editing}
          roles={roles}
          onSaved={async () => {
            setEditing(null);
            await load();
            await onChanged();
          }}
        />
      ) : (
        <div className="notices__actions">
          {draft ? (
            <>
              <button type="button" className="btn" onClick={() => setEditing(draft)}>
                Modifier le brouillon v{draft.version}
              </button>
              <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void publish(draft)}>
                Publier v{draft.version}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn"
              onClick={() =>
                setEditing({
                  id: '',
                  version: (versions?.[0]?.version ?? 0) + 1,
                  // Pre-filled from the live wording: a correction is usually a small edit, and
                  // retyping it is how a clause gets accidentally dropped.
                  title: versions?.[0]?.title ?? { fr: '', en: '' },
                  body: versions?.[0]?.body ?? { fr: '', en: '' },
                  enforcement: versions?.[0]?.enforcement ?? 'acknowledge',
                  audienceRoles: versions?.[0]?.audienceRoles ?? [],
                  surfaces: (versions?.[0]?.surfaces as NoticeSurface[]) ?? ['portal', 'field'],
                  publicUrl: versions?.[0]?.publicUrl ?? null,
                  publishedAt: null,
                  supersededAt: null,
                  publishedBy: null,
                  editable: true,
                })
              }
            >
              Nouvelle version
            </button>
          )}
        </div>
      )}

      {/* Who has agreed, and — the useful half — who has not. */}
      {trace && (
        <section className="notices__trace">
          <h3>
            Acceptations — v{trace.version} · {trace.acknowledged} de {trace.owed}
          </h3>
          <table>
            <thead>
              <tr>
                <th>Personne</th>
                <th>Rôle</th>
                <th>Accepté</th>
                <th>Où</th>
              </tr>
            </thead>
            <tbody>
              {trace.people.map((person) => (
                <tr key={person.userId} className={person.acknowledgedAt ? '' : 'short'}>
                  <td>{person.name}</td>
                  <td>{person.roleKey}</td>
                  <td className="tnum">
                    {person.acknowledgedAt ? new Date(person.acknowledgedAt).toLocaleString('fr-CA') : '—'}
                  </td>
                  <td>{person.surface ? SURFACE_LABEL[person.surface] ?? person.surface : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section className="notices__versions">
        <h3>Versions</h3>
        <ul>
          {versions?.map((version) => (
            <li key={version.id || version.version}>
              v{version.version} ·{' '}
              {version.publishedAt
                ? `publié le ${new Date(version.publishedAt).toLocaleDateString('fr-CA')}${version.publishedBy ? ` par ${version.publishedBy}` : ''}`
                : 'brouillon'}
              {version.supersededAt && ' · remplacé'}
              <span className="notices__enforce">{ENFORCEMENT_LABEL[version.enforcement]}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function NoticeEditor({
  noticeId,
  existing,
  roles,
  onSaved,
}: {
  noticeId?: string;
  existing?: Version;
  roles: Array<{ key: string; name: string }>;
  onSaved: () => void | Promise<void>;
}) {
  const [key, setKey] = useState('');
  const [kind, setKind] = useState<NoticeKind>('note');
  const [titleFr, setTitleFr] = useState(existing?.title.fr ?? '');
  const [titleEn, setTitleEn] = useState(existing?.title.en ?? '');
  const [bodyFr, setBodyFr] = useState(existing?.body.fr ?? '');
  const [bodyEn, setBodyEn] = useState(existing?.body.en ?? '');
  const [enforcement, setEnforcement] = useState(existing?.enforcement ?? 'acknowledge');
  const [audience, setAudience] = useState<string[]>(existing?.audienceRoles ?? []);
  const [surfaces, setSurfaces] = useState<NoticeSurface[]>(existing?.surfaces ?? ['portal', 'field']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const payload = useMemo(
    () => ({
      title: { fr: titleFr, en: titleEn || titleFr },
      body: { fr: bodyFr, en: bodyEn || bodyFr },
      enforcement,
      audienceRoles: audience,
      surfaces,
    }),
    [titleFr, titleEn, bodyFr, bodyEn, enforcement, audience, surfaces],
  );

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!noticeId) {
        await request('/notices', { method: 'POST', body: JSON.stringify({ key, kind, ...payload }) });
      } else if (existing?.id) {
        await request(`/notices/versions/${existing.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
      } else {
        await request(`/notices/${noticeId}/versions`, { method: 'POST', body: JSON.stringify(payload) });
      }
      await onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Enregistrement impossible');
    } finally {
      setBusy(false);
    }
  };

  const toggle = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

  return (
    <div className="notices__editor">
      {!noticeId && (
        <>
          <label>
            <span>Clé</span>
            <input value={key} onChange={(event) => setKey(event.target.value)} placeholder="rule.winter-roof" />
            <small>Identifiant stable, en minuscules. Ne change jamais, même si le titre change.</small>
          </label>
          <label>
            <span>Type</span>
            <select value={kind} onChange={(event) => setKind(event.target.value as NoticeKind)}>
              {NOTICE_KINDS.map((value) => (
                <option key={value} value={value}>
                  {KIND_LABEL[value]}
                </option>
              ))}
            </select>
          </label>
        </>
      )}

      <label>
        <span>Titre (français)</span>
        <input value={titleFr} onChange={(event) => setTitleFr(event.target.value)} />
      </label>
      <label>
        <span>Titre (anglais)</span>
        <input value={titleEn} onChange={(event) => setTitleEn(event.target.value)} placeholder="Repris du français si vide" />
      </label>
      <label>
        <span>Texte (français)</span>
        <textarea rows={10} value={bodyFr} onChange={(event) => setBodyFr(event.target.value)} />
      </label>
      <label>
        <span>Texte (anglais)</span>
        <textarea rows={6} value={bodyEn} onChange={(event) => setBodyEn(event.target.value)} />
      </label>

      <fieldset>
        <legend>Portée</legend>
        {/* Only the field app tracks position — an avis de localisation belongs there alone. */}
        {NOTICE_SURFACES.map((surface) => (
          <label key={surface} className="inline">
            <input
              type="checkbox"
              checked={surfaces.includes(surface)}
              onChange={() => setSurfaces(toggle(surfaces, surface))}
            />
            {SURFACE_LABEL[surface]}
          </label>
        ))}
        <small>Où l’avis s’affiche. L’application terrain est la seule qui transmet la position.</small>
      </fieldset>

      <fieldset>
        <legend>Qui doit l’accepter</legend>
        {roles.map((role) => (
          <label key={role.key} className="inline">
            <input
              type="checkbox"
              checked={audience.includes(role.key)}
              onChange={() => setAudience(toggle(audience, role.key))}
            />
            {role.name}
          </label>
        ))}
        <small>Aucun rôle coché : tout le monde dans l’entreprise.</small>
      </fieldset>

      <fieldset>
        <legend>Effet</legend>
        {(['blocking', 'acknowledge', 'informational'] as const).map((value) => (
          <label key={value} className="inline">
            <input type="radio" checked={enforcement === value} onChange={() => setEnforcement(value)} />
            {ENFORCEMENT_LABEL[value]}
          </label>
        ))}
      </fieldset>

      {error && <p className="notices__error">{error}</p>}

      <div className="notices__actions">
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy || !titleFr.trim() || !bodyFr.trim() || (!noticeId && key.trim().length < 2)}
          onClick={() => void save()}
        >
          Enregistrer le brouillon
        </button>
        <small>
          Rien n’est visible tant que le brouillon n’est pas publié. Publier demande une nouvelle acceptation à
          tout le personnel visé.
        </small>
      </div>
    </div>
  );
}
